/**
 * ArchiveToolbar — sticky filter + search + sort bar sitting under the hero.
 * Mirrors the homepage aesthetic (red rules, mono chips, dark surface) and
 * keeps every existing capability of ArchiveFilters (typeahead, keyboard
 * nav, category chips, sort select, live count).
 *
 * Keeps existing data-testids: archive-search-input, archive-sort-select,
 * archive-search-suggestions, archive-search-suggestion-<i>, filter-<name>.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X, Archive as ArchiveIcon, ChevronDown, Pin, PinOff } from "lucide-react";
import { globalSearch } from "../../lib/api";

const SORTS = [
  { id: "controversy", label: "Most Controversial" },
  { id: "newest",      label: "Newest" },
  { id: "views",       label: "Most Viewed" },
];

// localStorage key for the OPEN CABINET drawer preference. Kept in a
// single constant so any future consumer (e.g. keyboard shortcut,
// admin tool) can read/write the same slot without drift.
const CABINET_LS_KEY = "archive.cabinet.open";
// localStorage key for the toolbar PIN preference (sticky vs scroll-away).
const PIN_LS_KEY = "archive.toolbar.pinned";

export const ArchiveToolbar = ({
  search, sort, category, categories, topicCount, onParamChange,
}) => {
  const [q, setQ] = useState(search || "");
  const [groups, setGroups] = useState({ dossiers: [], people: [], organizations: [], operations: [] });
  const [flat, setFlat] = useState([]); // flattened list for keyboard cursor
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  // Focus guard — the suggestion dropdown must never spring open on
  // URL-derived searches (page loads with `?search=CIA` etc.) because
  // it then overlays the Oracle panel below and steals every pointer
  // event. It only opens when the user is actively focused on the
  // input.
  const focusedRef = useRef(false);

  useEffect(() => { setQ(search || ""); }, [search]);

  // ---------- Pin / unpin (localStorage-persisted, default pinned) ----------
  const [pinned, setPinned] = useState(() => {
    try { return window.localStorage.getItem(PIN_LS_KEY) !== "0"; }
    catch { return true; }
  });
  const togglePin = () => {
    setPinned((v) => {
      try { window.localStorage.setItem(PIN_LS_KEY, v ? "0" : "1"); } catch { /* ignore */ }
      return !v;
    });
  };

  // Sticky offset must equal the REAL header height (it varies by viewport
  // width and compacts on scroll) — a hard-coded 73px let the workbench
  // slide underneath the header. Measure it live instead.
  const [topOffset, setTopOffset] = useState(73);
  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return undefined;
    const update = () =>
      setTopOffset(Math.max(0, Math.round(header.getBoundingClientRect().height)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    window.addEventListener("resize", update, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // ---------- Cabinet drawer state (localStorage-persisted) ----------
  // Default: CLOSED. The user replaced the always-visible category
  // grid with an explicit "OPEN CABINET" drawer so first-time visitors
  // see a tidy toolbar; regulars who prefer chips-always-on will have
  // their preference restored from localStorage on subsequent visits.
  const [cabinetOpen, setCabinetOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.localStorage.getItem(CABINET_LS_KEY);
      if (raw === "1") return true;
      if (raw === "0") return false;
    } catch { /* ignore quota / privacy errors */ }
    return false;
  });
  useEffect(() => {
    // Persist. When a category becomes active while the cabinet was
    // closed, we auto-open it so the user always sees which filter is
    // currently applied — but we DO NOT overwrite the persisted
    // preference in that case; toggling manually is what saves.
    try { window.localStorage.setItem(CABINET_LS_KEY, cabinetOpen ? "1" : "0"); } catch { /* ignore */ }
  }, [cabinetOpen]);
  // Auto-open the cabinet whenever the user has an active category
  // filter (either from a URL deeplink or after clicking a chip that
  // is later reset). Keeps the UI truthful about what's applied.
  useEffect(() => {
    if (category && category !== "All") setCabinetOpen(true);
  }, [category]);

  const toggleCabinet = () => setCabinetOpen((v) => !v);

  useEffect(() => {
    if (!q || q.length < 1) {
      setGroups({ dossiers: [], people: [], organizations: [], operations: [] });
      setFlat([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await globalSearch(q, 5);
        const g = {
          dossiers:      r.dossiers      || [],
          people:        r.people        || [],
          organizations: r.organizations || [],
          operations:    r.operations    || [],
        };
        setGroups(g);
        const flatList = [];
        g.dossiers.forEach((d)     => flatList.push({ kind: "dossier",      slug: d.slug, title: d.title,   sub: d.category }));
        g.people.forEach((p)       => flatList.push({ kind: "person",       slug: p.slug, title: p.name,    sub: `${p.cameo_count} appearances` }));
        g.organizations.forEach((o) => flatList.push({ kind: "organization", slug: o.slug, title: o.name,    sub: `${o.cameo_count} appearances` }));
        g.operations.forEach((o)   => flatList.push({ kind: "operation",    slug: o.slug, title: o.name,    sub: `${o.cameo_count} appearances` }));
        setFlat(flatList);
        // Only surface the dropdown when the input has focus — never
        // when the query was populated from the URL on page load.
        setOpen(focusedRef.current && flatList.length > 0);
        setCursor(0);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const _urlFor = (row) => {
    switch (row.kind) {
      case "dossier":      return `/topic/${row.slug}`;
      case "person":       return `/people/${row.slug}`;
      case "organization": return `/organizations/${row.slug}`;
      case "operation":    return `/operations/${row.slug}`;
      default:             return "/archive";
    }
  };

  const onSearchKey = (e) => {
    if (open && flat.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => Math.min(c + 1, flat.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); return; }
      if (e.key === "Enter") {
        e.preventDefault();
        const row = flat[cursor];
        if (row) { setOpen(false); navigate(_urlFor(row)); return; }
      }
      if (e.key === "Escape") { setOpen(false); return; }
    }
    if (e.key === "Enter") onParamChange("search", e.target.value);
  };

  const clearSearch = () => {
    setQ("");
    onParamChange("search", "");
    setOpen(false);
  };

  return (
    <>
    {/* Unpinned: small unobtrusive restore control pinned to the corner of
        the browsing area — evidence-board tack, not a giant FAB. */}
    {!pinned && (
      <button
        type="button"
        onClick={togglePin}
        title="Pin search & filters"
        aria-label="Pin search and filter controls"
        data-testid="archive-toolbar-pin-restore"
        className="fixed bottom-5 right-4 z-40 w-10 h-10 border flex items-center justify-center
                   text-[#A39E93] hover:text-[#D92D20] hover:border-[#D92D20] transition-all
                   motion-safe:hover:-rotate-12 shadow-xl"
        style={{ borderColor: "#3A3630", background: "rgba(15,14,18,0.95)", backdropFilter: "blur(8px)" }}
      >
        <Pin size={15} />
      </button>
    )}
    <div
      data-testid="archive-toolbar"
      className={`${pinned ? "sticky" : "relative"} z-30 border-b transition-colors`}
      style={{
        top: pinned ? topOffset : undefined,
        borderColor: "#2D2A26",
        background: "rgba(11,10,13,0.92)",
        backdropFilter: "blur(10px)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-5">
        {/* Row 1 — search + sort + count */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 md:gap-4 items-center">
          <label className="relative block">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8377] pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => { focusedRef.current = true; if (q && flat.length) setOpen(true); }}
              onBlur={() => { focusedRef.current = false; setTimeout(() => setOpen(false), 180); }}
              placeholder="Search dossiers, tags, players, sources…"
              onKeyDown={onSearchKey}
              data-testid="archive-search-input"
              autoComplete="off"
              className="w-full bg-[#0B0A0D] border border-[#3A3630] focus:border-[#D92D20] outline-none pl-11 pr-11 py-3.5
                         font-mono text-[13px] text-[#E4DFD3] placeholder:text-[#7B7168] transition-colors"
            />
            {loading && (
              <Loader2 size={13} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7B7168] animate-spin" />
            )}
            {!loading && q && (
              <button
                type="button"
                onClick={clearSearch}
                data-testid="archive-search-clear"
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#7B7168] hover:text-[#D92D20] transition-colors"
              >
                <X size={13} />
              </button>
            )}
            {open && flat.length > 0 && (
              <div
                data-testid="archive-search-suggestions"
                className="absolute left-0 right-0 top-full mt-1 z-40 bg-[#0F0E12] border border-[#D92D20]/40 shadow-2xl max-h-[70vh] overflow-y-auto"
              >
                {[
                  { key: "dossiers",      label: "Dossiers",      items: groups.dossiers,      urlKind: "dossier" },
                  { key: "people",        label: "People",        items: groups.people,        urlKind: "person" },
                  { key: "organizations", label: "Organizations", items: groups.organizations, urlKind: "organization" },
                  { key: "operations",    label: "Operations",    items: groups.operations,    urlKind: "operation" },
                ].map(({ key, label, items, urlKind }) => {
                  if (!items.length) return null;
                  // Compute flat-index offset so cursor highlighting lines up.
                  let offset = 0;
                  if (urlKind === "person")       offset = groups.dossiers.length;
                  if (urlKind === "organization") offset = groups.dossiers.length + groups.people.length;
                  if (urlKind === "operation")    offset = groups.dossiers.length + groups.people.length + groups.organizations.length;
                  return (
                    <div key={key} data-testid={`search-group-${key}`}>
                      <div className="px-4 pt-3 pb-1 font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20] border-t first:border-t-0" style={{ borderColor: "#2D2A26" }}>
                        {label}
                      </div>
                      {items.map((it, i) => {
                        const globalIdx = offset + i;
                        const isCursor = globalIdx === cursor;
                        const title = it.title || it.name;
                        const sub = urlKind === "dossier"
                          ? (it.category || it.tagline || "")
                          : `${it.cameo_count} appearances`;
                        return (
                          <button
                            key={`${urlKind}-${it.slug}`}
                            data-testid={`archive-search-suggestion-${urlKind}-${it.slug}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setCursor(globalIdx)}
                            onClick={() => {
                              setOpen(false);
                              const url =
                                urlKind === "dossier"      ? `/topic/${it.slug}` :
                                urlKind === "person"       ? `/people/${it.slug}` :
                                urlKind === "organization" ? `/organizations/${it.slug}` :
                                                             `/operations/${it.slug}`;
                              navigate(url);
                            }}
                            className={`w-full text-left px-4 py-2.5 border-l-2 ${
                              isCursor ? "bg-[#1a0808] border-[#D92D20]" : "border-transparent hover:bg-[#1a0808]/50"
                            }`}
                          >
                            <div className="font-mono text-[12px] text-[#E4DFD3] truncate">{title}</div>
                            <div className="font-mono text-[10px] text-[#7B7168] truncate">{sub}</div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </label>

          <div className="flex items-center gap-3">
            <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#7B7168] hidden md:inline">
              Sort by
            </span>
            <select
              value={sort}
              onChange={(e) => onParamChange("sort", e.target.value)}
              data-testid="archive-sort-select"
              className="bg-[#0B0A0D] border border-[#3A3630] focus:border-[#D92D20] outline-none px-3 py-3
                         font-mono text-[11px] uppercase tracking-[0.25em] text-[#E4DFD3]"
            >
              {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em] text-[#8A8377]">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: topicCount > 0 ? "#3FB950" : "#7B7168" }}
            />
            <span data-testid="archive-toolbar-count">{topicCount} FILES</span>
            <button
              type="button"
              onClick={togglePin}
              title={pinned ? "Unpin — let controls scroll away" : "Pin controls while browsing"}
              aria-label={pinned ? "Unpin search controls" : "Pin search controls"}
              aria-pressed={pinned}
              data-testid="archive-toolbar-pin-toggle"
              className={`ml-1 p-1.5 border transition-all motion-safe:hover:-rotate-12 ${
                pinned ? "text-[#D92D20] border-[#D92D20]/50" : "text-[#7B7168] border-[#3A3630] hover:text-[#E4DFD3]"
              }`}
            >
              {pinned ? <Pin size={12} /> : <PinOff size={12} />}
            </button>
          </div>
        </div>

        {/* Row 2 — OPEN CABINET drawer (category filter) --------
            Replaces the previous always-visible category grid.
            Collapsed by default; opens on click; preference stored
            in localStorage. The active category is shown as a chip
            next to the toggle even when closed so users never lose
            sight of what filter is applied. */}
        <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t" style={{ borderColor: "#2D2A26" }}>
          <div className="flex items-center flex-wrap gap-3">
            <button
              type="button"
              onClick={toggleCabinet}
              aria-expanded={cabinetOpen}
              aria-controls="archive-cabinet-panel"
              data-testid="archive-cabinet-toggle"
              className="group inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.3em] transition-colors
                         border-[#3A3630] text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3]
                         focus:outline-none focus:border-[#D92D20] focus:text-[#E4DFD3]"
            >
              <ArchiveIcon size={12} className="opacity-90" />
              <span>{cabinetOpen ? "Close cabinet" : "Open cabinet"}</span>
              <ChevronDown
                size={12}
                className={`transition-transform duration-300 ${cabinetOpen ? "rotate-180" : "rotate-0"}`}
              />
            </button>

            {/* Active filter chip — visible even when the cabinet is
                collapsed so users always know what's applied.
                One-click to clear. */}
            {category && category !== "All" && (
              <span
                data-testid="archive-cabinet-active-chip"
                className="inline-flex items-center gap-2 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.28em]
                           bg-[#1a0808] border border-[#D92D20] text-[#E4DFD3]"
              >
                {category}
                <button
                  type="button"
                  onClick={() => onParamChange("category", "All")}
                  aria-label={`Clear ${category} filter`}
                  data-testid="archive-cabinet-active-clear"
                  className="text-[#D92D20] hover:text-white transition-colors"
                >
                  <X size={11} />
                </button>
              </span>
            )}

            {/* Silent counter — how many categories are inside */}
            <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.4em] text-[#7B7168] hidden sm:inline">
              {categories.length + 1} cabinets
            </span>
          </div>

          {/* Drawer — smooth reveal via CSS grid rows trick (no fixed
              max-height, adapts to any number of category chips). */}
          <div
            id="archive-cabinet-panel"
            data-testid="archive-cabinet-panel"
            role="region"
            aria-label="Category filter"
            aria-hidden={!cabinetOpen}
            className="grid transition-[grid-template-rows] duration-300 ease-out"
            style={{ gridTemplateRows: cabinetOpen ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <div className={`flex flex-wrap gap-2 pt-3 transition-opacity duration-300 ${cabinetOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                {["All", ...categories.map((c) => c.name)].map((c) => {
                  const active = category === c;
                  return (
                    <button
                      key={c}
                      onClick={() => onParamChange("category", c)}
                      data-testid={`filter-${c}`}
                      tabIndex={cabinetOpen ? 0 : -1}
                      className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em] border transition-colors
                                  ${active
                                    ? "bg-[#D92D20] border-[#D92D20] text-white"
                                    : "border-[#3A3630] text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3]"}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ArchiveToolbar;
